/**
 * Domain types and helpers for the media library.
 *
 * A media item is identified by its **sha256**, matching how Blossom
 * addresses blobs. That gives dedupe (re-adding a file is a no-op),
 * integrity checking, and a stable key for "which servers hold this?"
 * without extra bookkeeping.
 */

export type MediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'other';

/** A Blossom server that has confirmed it holds this blob. */
export interface MediaUpload {
  /** The server's base URL, as configured in the user's BUD-03 list. */
  server: string;
  /** The public URL returned by the server for this blob. */
  url: string;
  /** When the upload completed (ms since epoch). */
  uploadedAt: number;
}

export interface MediaItem {
  sha256: string;
  /**
   * The Nostr account (hex pubkey) that owns this library entry. Media
   * is scoped per account: two accounts sharing a browser each get their
   * own library, and the `/media` page never shows one account's files to
   * another. `''` means anonymous — added while logged out, or a legacy
   * item from before scoping existed; those are claimed by the first
   * account that opens the library while logged in.
   *
   * Note the scoping lives in the *metadata* only. The file bytes and
   * thumbnails in IndexedDB are keyed by raw sha256 and shared across
   * accounts (content-addressed, so the same file is byte-identical
   * either way) — per-account copies would double disk usage for no
   * privacy gain, since the bytes themselves aren't identifying.
   */
  pubkey: string;
  /** Original filename, for display and for choosing a sensible alt-text default. */
  name: string;
  /** MIME type as reported by the browser. */
  type: string;
  kind: MediaKind;
  size: number;
  /** Pixel dimensions, when known (images and video only). */
  width?: number;
  height?: number;
  /** Playback length in seconds, for audio/video. */
  duration?: number;
  /** Free-form tags. Replaces the folder model — an item can carry any number. */
  tags: string[];
  /** Accessibility description, nudged (not required) at insert time. */
  alt?: string;
  addedAt: number;
  /** Every Blossom server known to hold this blob. Empty means local-only. */
  uploads: MediaUpload[];
  /** False once the full bytes have been evicted to reclaim space; the thumbnail may still exist. */
  hasBlob: boolean;
}

/** True when this item exists on at least one Blossom server, and so is safe to reference from a published article. */
export function isUploaded(item: MediaItem): boolean {
  return item.uploads.length > 0;
}

/** The best public URL for an item, or undefined if it's local-only. */
export function publicUrl(item: MediaItem): string | undefined {
  return item.uploads[0]?.url;
}

export function mediaKindFromType(type: string): MediaKind {
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  return 'other';
}

/**
 * The `accept` attribute for file inputs. Deliberately broad — Blossom
 * stores arbitrary blobs, so the library isn't limited to images; the
 * only difference between kinds here is how they're previewed and which
 * markup gets inserted.
 */
export const MEDIA_ACCEPT = 'image/*,video/*,audio/*,application/pdf';

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Computes a file's sha256 as lowercase hex — the same identifier
 * Blossom uses, so hashing locally lets us dedupe and check whether a
 * blob is already hosted before spending an upload.
 */
export async function hashFile(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const THUMB_MAX = 400;

/**
 * Generates a small JPEG preview for an image or video.
 *
 * The grid renders potentially dozens of items at once; decoding
 * full-size originals for each would be slow and memory-hungry, and
 * would stop working entirely for items whose bytes have been evicted.
 * Returns undefined for kinds with nothing to rasterize (audio, PDF) —
 * those fall back to an icon.
 */
export async function generateThumbnail(file: Blob, kind: MediaKind): Promise<Blob | undefined> {
  try {
    if (kind === 'image') return await thumbFromImage(file);
    if (kind === 'video') return await thumbFromVideo(file);
  } catch {
    // A thumbnail is an optimization, never a requirement — a failure
    // here must not block adding the file to the library.
  }
  return undefined;
}

function drawToBlob(source: CanvasImageSource, width: number, height: number): Promise<Blob | undefined> {
  const scale = Math.min(1, THUMB_MAX / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(undefined);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob ?? undefined), 'image/jpeg', 0.8);
  });
}

async function thumbFromImage(file: Blob): Promise<Blob | undefined> {
  // createImageBitmap decodes off the main thread where supported, and
  // avoids the object-URL dance entirely.
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    try {
      return await drawToBlob(bitmap, bitmap.width, bitmap.height);
    } finally {
      bitmap.close();
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not decode image.'));
      el.src = url;
    });
    return await drawToBlob(img, img.naturalWidth, img.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function thumbFromVideo(file: Blob): Promise<Blob | undefined> {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = url;

    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('Could not decode video.'));
    });

    // Seek slightly in: the very first frame of many videos is black.
    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
      video.currentTime = Math.min(0.5, (video.duration || 1) / 4);
      // Some browsers never fire `seeked` for tiny/odd files.
      setTimeout(resolve, 1500);
    });

    return await drawToBlob(video, video.videoWidth || THUMB_MAX, video.videoHeight || THUMB_MAX);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Reads intrinsic dimensions / duration, for display and for `dim` metadata. */
export async function probeMedia(
  file: Blob,
  kind: MediaKind,
): Promise<{ width?: number; height?: number; duration?: number }> {
  try {
    if (kind === 'image') {
      if (typeof createImageBitmap === 'function') {
        const bitmap = await createImageBitmap(file);
        const dims = { width: bitmap.width, height: bitmap.height };
        bitmap.close();
        return dims;
      }
      return {};
    }

    if (kind === 'video' || kind === 'audio') {
      const url = URL.createObjectURL(file);
      try {
        const el = document.createElement(kind === 'video' ? 'video' : 'audio');
        el.preload = 'metadata';
        el.src = url;
        await new Promise<void>((resolve, reject) => {
          el.onloadedmetadata = () => resolve();
          el.onerror = () => reject(new Error('Could not read media metadata.'));
        });
        const media = el as HTMLVideoElement;
        return {
          width: media.videoWidth || undefined,
          height: media.videoHeight || undefined,
          duration: Number.isFinite(media.duration) ? media.duration : undefined,
        };
      } finally {
        URL.revokeObjectURL(url);
      }
    }
  } catch {
    // Metadata is nice-to-have; never block on it.
  }
  return {};
}

/* ---------------- tags ---------------- */

/** Normalizes a tag for storage/comparison: trimmed, lowercased, internal whitespace collapsed. */
export function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function addTag(tags: string[], tag: string): string[] {
  const normalized = normalizeTag(tag);
  if (!normalized || tags.includes(normalized)) return tags;
  return [...tags, normalized].sort();
}

export function removeTag(tags: string[], tag: string): string[] {
  return tags.filter((t) => t !== tag);
}

/** Every distinct tag across the library, with usage counts, for the filter bar. */
export function collectTags(items: MediaItem[]): { tag: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    for (const tag of item.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}
